import { useTranslations } from '@lang';
import moment from 'moment';
import { useCallback, useMemo } from 'react';
import type { JourneyPickerLabels, JourneyPickerSearchParams } from 'voyager';
import { JourneyPicker } from 'voyager';
import { getDiningCovers, getDiningPartyChange } from '@features/tables/diningParty';
import { getMondayFirstWeekdays } from '@utils/date';
import type { OnChangeParams } from '../JourneyPicker/JourneyPicker.types';
import { CenterWrapper } from './JourneyPickerV2.styles';
import type { JourneyPickerV2Props } from './JourneyPickerV2.types';

export type {
  DateContext,
  JourneyPickerSearchParams,
  JourneyPickerV2Props,
} from './JourneyPickerV2.types';

function useJourneyPickerLabels(): JourneyPickerLabels {
  const { t } = useTranslations();

  return useMemo(() => {
    const months = moment.localeData().months();

    return {
      selectDate: t('spa.selectDate'),
      selectDates: t('common.dates'),
      search: t('spaClasses.search'),
      browse: t('common.category'),
      edit: t('common.edit'),
      heading: t('spaClasses.changeSearch'),
      changeDate: t('common.calendar.changeDateCTA'),
      changeGuests: t('common.guests'),
      update: t('personalization.update'),
      back: t('common.back'),
      guests: (total: number) => t('common.numberOfGuests', { guestsAmount: total }),
      datePicker: {
        weekdays: getMondayFirstWeekdays(),
        months,
        date: t('common.date'),
        checkIn: t('guestsPicker.checkin'),
        checkOut: t('guestsPicker.checkout'),
        selectDate: t('spa.selectDate'),
        previousMonth: t('aria.previousMonth'),
        nextMonth: t('aria.nextMonth'),
        loadEarlier: t('common.calendar.loadEarlier'),
        loadMore: t('common.calendar.loadMore'),
        apply: t('basket.apply'),
      },
      guestPicker: {
        guests: t('guestsPicker.label'),
        adults: t('guestsPicker.adultsLabel'),
        children: t('guestsPicker.childrenLabel'),
        summary: (adults: number, children: number) =>
          t('guestsPicker.value', {
            numberOfAdults: adults,
            numberOfChildren: children,
          }),
      },
      rateLabels: {
        pill: t('common.rate'),
        placeholder: t('room.specialCodes'),
        apply: t('basket.apply'),
        clear: t('common.clear'),
      },
    };
  }, [t]);
}

export function JourneyPickerV2({
  dateMode = 'range',
  dateContext = 'stay',
  showGuests,
  maxGuests,
  categories,
  category,
  onCategoryChange,
  disableCategorySelect,
  journeyParams,
  changeJourneyParam,
  onChange,
  onSearchComplete,
  columnView,
  numberOfMonths,
  availability,
  isLoadingAvailability,
  onMonthChange,
  showRate,
  rateCode,
  onRateCodeChange,
  imageOverlay = false,
}: JourneyPickerV2Props) {
  const labels = useJourneyPickerLabels();

  const defaultFromDate =
    dateContext === 'spa' || dateContext === 'dining'
      ? journeyParams.spaDate?.format('YYYY-MM-DD') ||
        journeyParams.fromDate?.format('YYYY-MM-DD') ||
        ''
      : journeyParams.fromDate?.format('YYYY-MM-DD') || '';

  const defaultToDate = journeyParams.toDate?.format('YYYY-MM-DD') || '';

  const defaultSpaDate = journeyParams.spaDate?.format('YYYY-MM-DD') || '';

  const handleSearch = useCallback(
    (params: JourneyPickerSearchParams) => {
      if (dateContext === 'spa') {
        changeJourneyParam('spaAdults', params.adults);
      } else if (dateContext === 'dining') {
        const partyChange = getDiningPartyChange(params.adults, {
          adults: journeyParams.adults,
          children: journeyParams.children,
        });
        if (partyChange) {
          changeJourneyParam('adults', partyChange.adults);
          changeJourneyParam('children', partyChange.children);
          changeJourneyParam('rooms', [{ roomNumber: 1, ...partyChange }]);
          changeJourneyParam('childAges', []);
        }
      } else {
        changeJourneyParam('adults', params.adults);
        changeJourneyParam('children', params.children);
      }
      changeJourneyParam('fromDate', params.fromDate);
      changeJourneyParam('spaDate', params.fromDate);
      changeJourneyParam('diningDate', params.fromDate);
      if (params.toDate) {
        changeJourneyParam('toDate', params.toDate);
      } else if (
        defaultToDate &&
        moment(params.fromDate).isSameOrAfter(moment(defaultToDate), 'day')
      ) {
        changeJourneyParam('toDate', moment(params.fromDate).add(2, 'days').format());
      }

      if (onChange) {
        const changeParams: OnChangeParams = {
          adults: params.adults ?? journeyParams.adults,
          children: params.children ?? journeyParams.children,
          fromDate: dateContext === 'stay' ? params.fromDate : defaultFromDate,
          toDate: dateContext === 'stay' ? params.toDate : defaultToDate,
          spaDate: dateContext === 'spa' ? params.fromDate : defaultSpaDate,
        };
        onChange(changeParams);
      }

      onSearchComplete?.(params);
    },
    [
      dateContext,
      changeJourneyParam,
      onChange,
      onSearchComplete,
      journeyParams.adults,
      journeyParams.children,
      defaultFromDate,
      defaultToDate,
      defaultSpaDate,
    ]
  );

  return (
    <CenterWrapper $imageOverlay={imageOverlay}>
      <JourneyPicker
        dateMode={dateMode}
        showGuests={showGuests}
        guestMode={dateContext === 'stay' ? 'split' : 'combined'}
        maxAdults={maxGuests}
        categories={categories}
        category={category}
        onCategoryChange={onCategoryChange}
        disableCategorySelect={disableCategorySelect}
        defaultFromDate={defaultFromDate}
        defaultToDate={dateMode === 'range' ? defaultToDate : undefined}
        defaultAdults={
          dateContext === 'spa'
            ? journeyParams.spaAdults
            : dateContext === 'dining'
            ? getDiningCovers(journeyParams)
            : journeyParams.adults
        }
        defaultChildren={dateContext === 'stay' ? journeyParams.children : 0}
        columnView={columnView}
        numberOfMonths={numberOfMonths}
        availability={availability}
        isLoadingAvailability={isLoadingAvailability}
        showPastDates
        onMonthChange={onMonthChange}
        showRate={showRate}
        rateCode={rateCode}
        onRateCodeChange={onRateCodeChange}
        onSearch={handleSearch}
        showSearchButton={false}
        imageOverlay={imageOverlay}
        labels={labels}
      />
    </CenterWrapper>
  );
}
